import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faVideo } from '@fortawesome/pro-light-svg-icons/faVideo';
import clsx from 'clsx';
import type { OutreachPointType } from 'fym-shared/src/music24/outreach/types';
import { TypedMemo } from '../../../common/types';
import type { TargetRow } from '../outreachTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ICON: Partial<Record<OutreachPointType, IconDefinition>> = {
  instagram: faUser,
  tiktok: faUser,
  youtube: faVideo,
  website: faArrowUpRightFromSquare,
  form: faArrowUpRightFromSquare,
};

/**
 * The contact icons of a target (design outreach c1 note): green when a published address or
 * handle exists (the value stays hidden until a reveal), dashed when none is known.
 */
const ContactIconsComponent: React.FC<{ contact: TargetRow['contact'] }> = ({ contact }) => {
  const others = useMemo(
    () =>
      (contact?.pointTypes ?? [])
        .filter((type) => type !== 'email' && ICON[type] !== undefined)
        .slice(0, 2)
        .map((type) => (
          <span key={type} className={styles.contactIcon}>
            <FontAwesomeIcon icon={ICON[type] ?? faUser} />
          </span>
        )),
    [contact]
  );
  const hasEmail = contact?.hasEmail === true;
  return (
    <span className={styles.contactIcons} data-testid={hasEmail ? 'contact-has-email' : 'contact-no-email'}>
      <span className={clsx(styles.contactIcon, !hasEmail && styles.contactIconOff)}>
        <FontAwesomeIcon icon={faEnvelope} />
      </span>
      {others}
    </span>
  );
};

export const ContactIcons = TypedMemo(ContactIconsComponent);
